import {computed, observable, override} from "mobx";
import {BaseMobxState} from "../../../infrastructure/mobx/base-mobx-state";
import {NavigationManager} from "../../../core/managers/navigation-manager";
import {AppStore} from "../../../core/app/app-store";
import {LoggerUtils} from "../../../infrastructure/utils/logger/logger-utils";
import {DatetimeUtils} from "../../../infrastructure/utils/datetime-utils";
import {DeviceViewModel} from "../../../core/models/device.view-model";
import {TermsUtils} from "../../../infrastructure/utils/terms-utils";
import {BoxScoresWidgetConfig} from "../box-scores-widget.config";

import {LineupsWidgetState} from "../../lineups-widget/models/lineups-widget.state";
import {BoxScoresTableViewModel} from "./box-scores.table.view-model";
import {PlayersStatisticsTableDomainModel} from "../../../domain/yard/lineups/players-statistics/tables/players-statistics.table.domain-model";
import {PlayersStatisticsCategoriesDomainModel} from "../../../domain/yard/lineups/players-statistics/categories/players-statistics.categories.domain-model";
import {PlayersStatisticsTableGroupDomainModel} from "../../../domain/yard/lineups/players-statistics/tables/groups/players-statistics.table.group.domain-model";
import {BOX_SCORES_WIDGET} from "../box-scores-widget-consts";
import {BoxScoresWidgetTerms} from "../box-scores-widget.terms";
import {GAME_STATUSES} from "../../../core/app/consts";

export class BoxScoresWidgetState extends LineupsWidgetState {
  static injectables = [...BaseMobxState.injectables, NavigationManager, AppStore, LoggerUtils, DatetimeUtils, DeviceViewModel, TermsUtils, BoxScoresWidgetConfig];

  config: BoxScoresWidgetConfig;
  terms: BoxScoresWidgetTerms;
  @observable showAllStats: Boolean = true;

  @computed get tables(): BoxScoresTableViewModel {
    const tables: Array<BoxScoresTableViewModel> = [];
    const categoriesTables = this.categoriesTables;
    const groupsTables = this.groupsTables;

    if (categoriesTables?.length > 0) {
      tables.push(...categoriesTables);
    }

    if (groupsTables?.length > 0) {
      tables.push(...groupsTables);
    }

    if(this.tablesDM?.length > 0 && tables.length === 0) {
      const tableVM: BoxScoresTableViewModel = new BoxScoresTableViewModel(null, this.tablesDM[0], this.showAllStats);
      tables.push(tableVM)
    }

    return tables;
  }

  get isExpandable(): Boolean {
    return this.selectedTeam.playersStatistics?.isExpandable;
  }

  get selectedTab(): Boolean {
    const selectedTab: Boolean = this.isHomeTeamSelected ? BOX_SCORES_WIDGET.COMPETITORS.home : BOX_SCORES_WIDGET.COMPETITORS.away;

    return selectedTab;
  }

  @computed get tableCellWidthDesktop(): Number {
    return this.config.tableCellWidthDesktop
  }

  @computed get categoriesTables(): Array<BoxScoresTableViewModel> {
    const categoriesTables: Array<BoxScoresTableViewModel> = this.categoriesDM?.length > 0 && this.tablesDM?.length > 0 && this.categoriesDM.map((category: PlayersStatisticsCategoriesDomainModel) => {
      const tablesDM: PlayersStatisticsTableDomainModel = this.tablesDM?.find((table: PlayersStatisticsTableDomainModel) => category.id === table.categoryId);
      if(!tablesDM) {
        return null;
      }
      const tableVM: BoxScoresTableViewModel = new BoxScoresTableViewModel(category, tablesDM, this.showAllStats);

      return tableVM;
    }).filter(t => !!t);

    return categoriesTables || [];
  }

  @computed get groupsTables(): Array<BoxScoresTableViewModel> {
    const groupsTables: Array<BoxScoresTableViewModel> = [];
    const isMajorValues = !this.showAllStats && this.device.isSmallView;

    this.tablesDM?.forEach((table: PlayersStatisticsTableDomainModel) => {
      table.groups?.forEach((group: PlayersStatisticsTableGroupDomainModel) => {
        const tableVM: BoxScoresTableViewModel = new BoxScoresTableViewModel(group, table, isMajorValues);

        groupsTables.push(tableVM);
      });
    });

    return groupsTables;
  }

  @computed get tablesDM(): Array<PlayersStatisticsTableDomainModel> {
    const tables: Array<PlayersStatisticsTableDomainModel> = this.selectedTeam.playersStatistics?.domainModel.tables;

    return tables
  }

  @computed get categoriesDM(): Array<PlayersStatisticsCategoriesDomainModel> {
    const categories: Array<PlayersStatisticsCategoriesDomainModel> = this.selectedTeam.playersStatistics?.domainModel.categories;

    return categories
  }

  @computed get shouldRenderEntityImage(): Boolean {
    const shouldRenderEntityImage: Boolean = this.device.screenWidth > 320; //TODO: consts

    return shouldRenderEntityImage;
  }

  @override get widgetTitle(): String {
    const widgetTitle: String = this.dataSource?.game.hasPreMatchMissingPlayers ? this.terms.LINEUPS_MISSING : this.terms.WEB_BOXSCORE;

    return widgetTitle;
  }

  @override get headerTitle(): String {
    const headerDescription: String = this.terms.WEB_BOXSCORE_INPAGE_TITLE.format({
      BOXSCORE: this.terms.WEB_BOXSCORE,
      WEB_MATCHUP_INPAGE_TITLE_TEMPLATE: this.terms.WEB_MATCHUP_INPAGE_TITLE_TEMPLATE.format({
        HOME_COMPETITOR: this.dataSource?.game.homeCompetitor.shortName || this.dataSource?.game.homeCompetitor.name,
        GAME_TITLE_VS: this.terms.GAME_TITLE_VS,
        AWAY_COMPETITOR: this.dataSource?.game.awayCompetitor.shortName || this.dataSource?.game.awayCompetitor.name,
      })
    })

    return headerDescription;
  }

  @override get headerDescription(): String {
    const headerDescription: String = this.terms.WEB_BOXSCORE_INPAGE_DESCRIPTION.format({
      COMPETITION_NAME: this.dataSource?.game.competition?.name || this.dataSource?.game.competition?.shortName
    })

    return headerDescription;
  }

  @computed get shouldPlayerClickable(): Boolean {
    const sportTypes: Array<Number> = Object.values(BOX_SCORES_WIDGET.SPORT_TYPES_PLAYER_STATS_CARD_DISPLAY);
    const validStatuses: Array<GAME_STATUSES> = [GAME_STATUSES.started, GAME_STATUSES.ended];
    const shouldPlayerClickable: Boolean = validStatuses.includes(this.dataSource?.game?.statusGroup) && sportTypes.includes(this.dataSource?.game.sport.id)

    return !this.appState.settings.isWidget && shouldPlayerClickable;
  }
}
